import { JSXElementConstructor, memo } from "react";
import CloseIcon from "../../arbiter-icon/x-close.svg";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoButton } from "../buttons/redo-button";
import { RedoIcon } from "../icon/redo-icon";
import { RedoTextInput } from "../input/redo-text-input";
import { RedoChatText, RedoChatTextProps } from "./redo-chat-text";
import * as RedoChatCss from "./redo-chat.module.css";
import { RedoTyping } from "./redo-typing";

export interface RedoChatProps {
  title: string;
  LeadingIcon?: JSXElementConstructor<any>;
  close: () => void;
  chatResponderName: string; //This is the chatter on the left side of the widget
  chatHistory: { key: string; text: string; isResponder: boolean }[]; //The order of the chat history is respected
  textValue: string;
  setTextValue: (textValue: string) => void;
  submit: () => void;
  responderTyping?: boolean;
  fullWidth?: boolean;
  suggestions?: string[];
}

export const RedoChat = memo(function RedoChat({
  title,
  LeadingIcon,
  close,
  chatResponderName,
  chatHistory,
  textValue,
  setTextValue,
  submit,
  responderTyping,
  fullWidth,
  suggestions,
}: RedoChatProps) {
  function getGroupedChats(
    chatHistory: { key: string; text: string; isResponder: boolean }[],
  ): RedoChatTextProps[] {
    if (chatHistory.length === 0) {
      return [];
    }
    const chats: RedoChatTextProps[] = [];
    let currentChat: RedoChatTextProps = {
      isResponder: chatHistory[0].isResponder,
      name: chatHistory[0].isResponder ? chatResponderName : undefined,
      chatBlock: [chatHistory[0].text],
    };
    for (let i = 1; i < chatHistory.length; i++) {
      const chat = chatHistory[i];
      if (chat.isResponder === currentChat.isResponder) {
        currentChat.chatBlock.push(chat.text);
      } else {
        chats.push(currentChat);
        currentChat = {
          isResponder: chat.isResponder,
          name: chat.isResponder ? chatResponderName : undefined,
          chatBlock: [chat.text],
        };
      }
    }
    chats.push(currentChat);
    return chats;
  }
  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      className={RedoChatCss.main}
      flexDirection="column"
      gap="xl"
      maxHeight="three-quarter-screen"
      p="3xl"
      radius="md"
      w={fullWidth ? "full" : undefined}
    >
      <Flex align="center" flexDirection="row" justify="space-between">
        <Flex align="center">
          {LeadingIcon && <RedoIcon Icon={LeadingIcon} size={16} />}
          <Text fontSize="xs" fontWeight="medium">
            {title}
          </Text>
        </Flex>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={CloseIcon}
          onClick={close}
          size="md"
        />
      </Flex>

      <Flex
        className={RedoChatCss.textContainer}
        flexDirection="column-reverse"
      >
        <Flex flexDirection="column">
          <Flex flexDirection="column" gap="3xl" mb="md">
            {getGroupedChats(chatHistory).map((chatBlock, index) => (
              <RedoChatText
                chatBlock={chatBlock.chatBlock}
                isResponder={chatBlock.isResponder}
                key={index}
                name={chatBlock.isResponder ? chatResponderName : undefined}
              />
            ))}
          </Flex>
          {responderTyping && <RedoTyping />}
          {suggestions?.length && suggestions.length > 0 && (
            <Flex flexDirection="column">
              <Text fontSize="xs" fontStyle="italic" fontWeight="regular">
                Suggestions
              </Text>
              <Flex>
                {suggestions.map((value) => (
                  <Flex
                    bgColor="secondary"
                    borderColor="secondary"
                    borderStyle="solid"
                    borderWidth="1px"
                    className={RedoChatCss.suggestion}
                    hoverColor="primary"
                    key={value}
                    onClick={() => setTextValue(value)}
                    p="md"
                    radius="md"
                    textColor="secondary"
                  >
                    <Text fontSize="xs" fontWeight="thin">
                      {value}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      <RedoTextInput
        fullWidth
        inputTypeHint="text"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Ask me anything"
        setValue={setTextValue}
        size="lg"
        submit={() => submit()}
        value={textValue}
      />
    </Flex>
  );
});
